import { INudgeSymbolBragg } from "@spawn/nudge";
import { BaseSymbolTypes, symbolTypeBraggIds } from "../../services/symbolService";

/**
 * Generates nudging offsets for each reel based on nudge symbol responses.
 *
 * The function calculates how much each reel should be nudged based on the presence of scatter symbols
 * and the positions of reveal symbols.
 *
 * @param response - Array of nudge symbol data from Bragg, containing symbol type and positions
 * @returns Array of 5 numbers representing nudging offsets for each reel
 *
 * @example
 * ```typescript
 * const response = [
 *   { symbol: 101, reelPos: 0, linePos: 1 },
 *   { symbol: 102, reelPos: 2, linePos: 3 }
 * ];
 * const offsets = nrgGenerateBraggNudgingOffsets(response);
 * // Returns: [1, 0, 1, 0, 0] or similar based on scatter count
 * ```
 */
export function nrgGenerateBraggNudgingOffsets(response: INudgeSymbolBragg[]): number[] {
	const nudgingOffsets = [0, 0, 0, 0, 0];
	const extraScatterCount = response.filter((r) => symbolTypeBraggIds.get(r.symbol) === BaseSymbolTypes.ScatterFG).length;
	const offset = extraScatterCount > 0 ? -extraScatterCount : 1;

	for (const reveal of response) {
		// apply default nudge when finding a reveal
		nudgingOffsets[reveal.reelPos] = offset;

		// if the reveal symbol is on the last line and going down, don't set the offset as we no longer want to nudge that reel
		const isLastReelMystery = response.filter((r) => r.reelPos === reveal.reelPos).length === 1 && reveal.linePos === 3;
		if (isLastReelMystery && offset > 0) {
			nudgingOffsets[reveal.reelPos] = 0;
		}
	}

	return nudgingOffsets;
}

/**
 * Ensures each reel has exactly 4 mystery symbols by adding mysteries after the last existing one.
 *
 * This function modifies the reels in place. For each reel, if it has fewer than 4 mystery symbols,
 * it adds the required number of mysteries after the last existing mystery symbol. Reels with 4 (or
 * more) mysteries are left unchanged.
 *
 * @param reels - 2D array representing the reels, where each inner array is a reel and each string is a symbol type
 *
 * @example
 * ```typescript
 * const reels = [
 *   ["HV1", "Mystery", "LV1"],           // Only 1 mystery
 *   ["Mystery", "Mystery", "Mystery", "Mystery", "HV2"] // Already 4 mysteries
 * ];
 * ensure4BlockMysteries(reels);
 * // First reel becomes: ["HV1", "Mystery", "Mystery", "Mystery", "Mystery"]
 * // Second reel remains unchanged
 * ```
 */
export function ensure4BlockMysteries(reels: string[][]): void {
	for (const reel of reels) {
		const mysteryCount = reel.filter((symbol) => symbol === BaseSymbolTypes.Mystery).length;
		if (mysteryCount < 1 || mysteryCount >= 4) {
			continue;
		}
		const lastMysteryIndex = reel.lastIndexOf(BaseSymbolTypes.Mystery);
		const mysteriesToAdd = 4 - mysteryCount;
		// replace the following symbols with mysteries
		reel.splice(lastMysteryIndex + 1, mysteriesToAdd, ...new Array(mysteriesToAdd).fill(BaseSymbolTypes.Mystery));
	}
}

/**
 * Applies a Bragg slot face configuration to the current reels by converting numeric IDs to symbol types.
 *
 * This function transforms the current reels by replacing each symbol with the corresponding symbol type
 * from the slot face configuration. It uses the provided mapping to convert numeric symbol IDs from
 * the slot face to string symbol types.
 *
 * @param currentReels - 2D array of current reel symbols (strings) to be modified
 * @param slotFace - 2D array of numeric symbol IDs from Bragg representing the target configuration
 * @param symbolTypeBraggIds - Map that converts numeric Bragg symbol IDs to string symbol types
 * @returns Modified reels array with symbols replaced according to the slot face configuration
 */
export function nrgApplyBraggSlotfaceBeforeRestore(
	currentReels: string[][],
	slotFace: number[][],
	symbolTypeBraggIds: Map<number, string>
): string[][] {
	for (let x = 0; x < currentReels.length; x++) {
		for (let y = 0; y < currentReels[x].length; y++) {
			currentReels[x][y] = symbolTypeBraggIds.get(slotFace[x][y])!;
		}
	}

	return currentReels;
}
